import { useState, useEffect, useRef, useCallback } from 'react';
import supabase from '../../../lib/supabaseClient';

const FALLBACK_POLL_INTERVAL = 60000; // 60s fallback poll in case subscription dies

// Recent Activity answers ONE question: who is actually talking to us right now.
// So this is a whitelist, not a blacklist — a new event_type has to earn its way
// in rather than leak in by default. Everything terminal or clerical is absent on
// purpose: opening the panel to read "STOP", "no thank you" or "fuck you" wastes
// the tenant's time, and those leads are already unreachable.
const LEAD_EVENTS = [
  'first_reply',
  'lead_engaging',
  'lead_hot',
  'handoff_triggered',
  'booking_in_progress',
  'booking_confirmed',
  'pricing_inquiry',
  'timeline_mentioned',
  'high_sentiment',
  'lead_reengaged',
  'lead_qualified' // retired — kept so historical rows still render
];

// Not about a lead at all, so no engagement proof applies.
const SYSTEM_EVENTS = [
  'campaign_launched',
  'campaign_paused',
  'campaign_resumed',
  'bulk_upload_completed',
  // One row per transfer, not per lead, written by transfer_leads_by_bucket. lead_id is
  // NULL, which is what keeps it out of the per-lead dedup and the engagement gate. Worth
  // surfacing because a transfer resets every field that says a lead was ever warm and
  // re-messages them from scratch — until 2026-09-09 it left no trace anywhere at all.
  'leads_transferred'
];

const FEED_EVENTS = [...LEAD_EVENTS, ...SYSTEM_EVENTS];

// 'first_reply' is in the list but carries the engagement gate below, which is the only
// reason it is safe. The DB trigger fn_activity_messages writes it on the first inbound
// with no regard for what that message says — 2,058 rows in the 30 days to 2026-08-06,
// mostly "No" / "Stop" / "Wrong number" / "Fuck you". Gated on sentiment it is 171.
//
// It has to be here, because it is the ONLY row a good first reply is guaranteed to get.
// score-lead only promotes a single reply to 'Engaging' at sentiment >= 60, or 50-59 if
// it asked a question (index.ts ~L1956-1962), and lead_journey logs only TRANSITIONS —
// so a lead who opens with "Sure. 825k" (55, no question mark) sits at 'Warm Lead',
// never produces a lead_engaging row, and would be invisible forever. Measured over 14
// days: 11 of 23 leads in that 50-59 band had no feed event at all, including "2 million
// not a dollar less" and "$800,000 not negotiable!!".
//
// Never in FEED_EVENTS: ai_disabled, lead_disqualified, lead_opted_out, lead_lost,
// lead_snoozed, lead_unsnoozed, lead_updated, call_logged, manual_sms, ai_sms_sent.

// "X is engaging" is a claim about the LEAD, so the lead's own words have to back it up.
// score-lead promotes status to 'Engaging' off a SINGLE reply that asked a question or
// read positive (score-lead/index.ts ~L1956-1962), so the flag lands right after SurFox's
// second outbound and reads as though SurFox is the one engaging. Gating the flag here,
// not the ladder there — 'Engaging' status drives pipeline filters and metrics
// product-wide.
//
// The gate is SENTIMENT, not message count. Counting was tried first and fails both ways,
// measured 2026-08-06 on two different Mark B.s in the same tenant:
//   Mark Boyette  "No" then "No"            -> 2 messages, contextual sentiment 20
//   Mark Baylon   "Yes, at $680 cash it would!" -> 1 message,  contextual sentiment 70
// A count shows the first and hides the second. contextual_sentiment_score gets both
// right, and it is already computed per inbound message — nothing new to build.
const NEEDS_ENGAGEMENT_PROOF = ['lead_engaging', 'first_reply'];

// 0-100, 50 = neutral. Same midpoint score-lead's own ladder reasons about (its
// "positive" bar is 60). Validated against 14 days of live replies: every bare "No"
// landed NULL-35, while "$280k and its yours" (70), "510 k it's yours" (65), "Yes on
// 6months" (65), "Sure $500,000" (60) and "Call me" (60) all cleared it on ONE reply.
const MIN_ENGAGEMENT_SENTIMENT = 50;

// Web-widget page view recorded as an inbound message. Not something the lead said, so
// it must never count as evidence of engagement.
const PAGE_OPEN_SENTINEL = '__page_open__';

// Rows are dropped after fetching, so pull extra to still fill `limit` slots.
const OVERFETCH = 8;

// One row per lead, newest kept. A single lead legitimately generates several events —
// Kevin L. held 5 of the top 22 rows on 2026-08-06 (engaging, timeline x2, booking,
// handoff) — and a good first reply now produces first_reply AND lead_engaging. In a
// 5-slot panel that buries every other lead. Input must already be newest-first.
function oneRowPerLead(rows) {
  const seen = new Set();
  return rows.filter(r => {
    if (!r.lead_id) return true;           // system events are not per-lead
    if (seen.has(r.lead_id)) return false;
    seen.add(r.lead_id);
    return true;
  });
}

const DEMO_ACTIVITIES = [
  {
    id: 'demo-act-1',
    event_type: 'lead_hot',
    title: 'Lead marked HOT',
    description: 'Sarah Mitchell from Bright Smile Dental is ready to talk',
    priority: 'high',
    created_at: new Date(Date.now() - 2 * 60000).toISOString()
  },
  {
    id: 'demo-act-2',
    event_type: 'booking_confirmed',
    title: 'Appointment booked',
    description: 'Dr. James Wilson confirmed call for tomorrow 10am',
    priority: 'high',
    created_at: new Date(Date.now() - 8 * 60000).toISOString()
  },
  {
    id: 'demo-act-3',
    event_type: 'lead_engaging',
    title: 'Lead is engaging',
    description: 'Amanda Torres is going back and forth on her timeline',
    priority: 'medium',
    created_at: new Date(Date.now() - 25 * 60000).toISOString()
  },
  {
    id: 'demo-act-4',
    event_type: 'pricing_inquiry',
    title: 'Pricing question',
    description: 'Michael Chen asked about placement fees',
    priority: 'high',
    created_at: new Date(Date.now() - 45 * 60000).toISOString()
  },
  {
    id: 'demo-act-5',
    event_type: 'lead_qualified',
    title: 'Lead qualified',
    description: 'Lisa Rodriguez passed all qualification criteria',
    priority: 'medium',
    created_at: new Date(Date.now() - 90 * 60000).toISOString()
  }
];

/**
 * Hook for real-time activity feed
 * @param {number} limit - Number of items to show (default 5)
 * @param {string|null} selectedTenantId - Tenant override for admins
 * @returns {object} { activities, loading, error }
 */
export function useActivityFeed(limit = 5, selectedTenantId = null) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedTenantId, setResolvedTenantId] = useState(null);
  const isDemoRef = useRef(false);
  const channelRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Phase 1: resolve tenantId (async auth lookup happens once)
  useEffect(() => {
    if (selectedTenantId === 'all') {
      setResolvedTenantId('all');
      return;
    }
    if (selectedTenantId) {
      setResolvedTenantId(selectedTenantId);
      return;
    }
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      const id = user.app_metadata?.tenant_id || user.user_metadata?.tenant_id;
      if (!id) {
        console.error('[useActivityFeed] No tenant_id available');
        return;
      }
      setResolvedTenantId(id);
    });
  }, [selectedTenantId]);

  // Drop rows whose lead is not actually in a live conversation. Runs on the lead's
  // SETTLED state, which is the whole reason this is a read-time filter: the write-side
  // trigger fires on message INSERT, before the reply has been classified, so it could
  // only regex-guess whether "No" ends the thread.
  const keepRealConversations = useCallback(async (rows) => {
    if (!rows || rows.length === 0) return [];

    const leadIds = [...new Set(rows.map(r => r.lead_id).filter(Boolean))];
    if (leadIds.length === 0) return rows;

    // An opted-out lead is unreachable — nothing the tenant sees here is actionable.
    const { data: leads, error: leadError } = await supabase
      .from('leads')
      .select('id, sms_opted_out')
      .in('id', leadIds);
    if (leadError) throw leadError;
    const optedOut = new Set((leads || []).filter(l => l.sms_opted_out).map(l => l.id));

    // How warmly did the LEAD actually engage, for the events that claim they are engaging.
    // Best score across the thread, not the latest: a lead who warmed up once and then
    // answered a narrow question with "no" is still in the conversation.
    const proofIds = [...new Set(
      rows
        .filter(r => r.lead_id && NEEDS_ENGAGEMENT_PROOF.includes(r.event_type))
        .map(r => r.lead_id)
    )];
    const bestSentiment = new Map();
    if (proofIds.length > 0) {
      const { data: msgs, error: msgError } = await supabase
        .from('messages')
        .select('lead_id, contextual_sentiment_score')
        .in('lead_id', proofIds)
        .eq('direction', 'inbound')
        .neq('message_body', PAGE_OPEN_SENTINEL)
        .not('contextual_sentiment_score', 'is', null);
      if (msgError) throw msgError;
      (msgs || []).forEach(m => {
        const prev = bestSentiment.get(m.lead_id);
        if (prev === undefined || m.contextual_sentiment_score > prev) {
          bestSentiment.set(m.lead_id, m.contextual_sentiment_score);
        }
      });
    }

    return rows.filter(r => {
      if (!r.lead_id) return true;                       // system event
      if (optedOut.has(r.lead_id)) return false;
      if (!NEEDS_ENGAGEMENT_PROOF.includes(r.event_type)) return true;
      // No scored reply yet means no evidence yet. Either score-lead has not run (the
      // next 60s poll picks it up) or every reply was a bare rejection, which is the
      // case in all 12 unscored threads measured on 2026-08-06.
      const score = bestSentiment.get(r.lead_id);
      return score !== undefined && score >= MIN_ENGAGEMENT_SENTIMENT;
    });
  }, []);

  // Shared fetch function — used for initial load, visibility refetch, and fallback poll
  const fetchActivities = useCallback(async (tenantId) => {
    const isAll = tenantId === 'all';

    const buildQuery = (highMediumOnly) => {
      let q = supabase
        .from('activity_feed')
        .select('*')
        .in('event_type', FEED_EVENTS)
        .order('created_at', { ascending: false })
        .limit(limit * OVERFETCH);
      if (highMediumOnly) q = q.in('priority', ['high', 'medium']);
      if (!isAll) q = q.eq('tenant_id', tenantId);
      return q;
    };

    // Try high/medium priority first
    const { data, error: fetchError } = await buildQuery(true);
    if (fetchError) throw fetchError;
    let rows = await keepRealConversations(data || []);

    // Fallback: any priority, same whitelist and same engagement proof. A quiet panel
    // is the correct output for a tenant with no live conversations — better than
    // backfilling it with rows we just decided were not worth showing.
    if (rows.length === 0) {
      const { data: fbData, error: fbError } = await buildQuery(false);
      if (fbError) throw fbError;
      rows = await keepRealConversations(fbData || []);
    }

    return oneRowPerLead(rows).slice(0, limit);
  }, [limit, keepRealConversations]);

  // Subscribe to realtime channel
  const subscribe = useCallback((tenantId, cancelled) => {
    // Clean up previous channel if any
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const isAll = tenantId === 'all';
    const channelConfig = {
      event: 'INSERT',
      schema: 'public',
      table: 'activity_feed',
      ...(isAll ? {} : { filter: `tenant_id=eq.${tenantId}` })
    };

    const channel = supabase
      .channel(`activity-feed-${tenantId}-${Date.now()}`)
      .on(
        'postgres_changes',
        channelConfig,
        async (payload) => {
          if (cancelled.current) return;
          // Same whitelist + same engagement proof as the fetch path. Without this a
          // live INSERT would walk straight past both filters and put back exactly
          // what the panel is meant to exclude.
          if (!FEED_EVENTS.includes(payload.new.event_type)) return;
          try {
            const kept = await keepRealConversations([payload.new]);
            if (kept.length === 0 || cancelled.current) return;
            setActivities(prev => oneRowPerLead([payload.new, ...prev]).slice(0, limit));
          } catch (err) {
            console.error('[useActivityFeed] Realtime filter error:', err);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
    return channel;
  }, [limit, keepRealConversations]);

  // Phase 2: subscribe + fetch + set up resilience
  useEffect(() => {
    if (!resolvedTenantId) return;

    const cancelled = { current: false };

    async function init() {
      try {
        const isAll = resolvedTenantId === 'all';

        // Only check demo status for a specific tenant
        let isDemo = false;
        if (!isAll) {
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('is_demo')
            .eq('id', resolvedTenantId)
            .single();
          isDemo = tenantData?.is_demo;
        }

        if (isDemo) {
          isDemoRef.current = true;
          if (!cancelled.current) {
            setActivities(DEMO_ACTIVITIES);
            setLoading(false);
          }
          return;
        }

        isDemoRef.current = false;

        // Subscribe BEFORE fetch so no events are missed
        subscribe(resolvedTenantId, cancelled);

        const data = await fetchActivities(resolvedTenantId);
        if (!cancelled.current) {
          setActivities(data);
          setLoading(false);
        }

        // Fallback poll every 60s to catch events missed by a dead subscription
        pollIntervalRef.current = setInterval(async () => {
          if (cancelled.current || isDemoRef.current) return;
          try {
            const freshData = await fetchActivities(resolvedTenantId);
            if (!cancelled.current) setActivities(freshData);
          } catch (err) {
            console.error('[useActivityFeed] Poll error:', err);
          }
        }, FALLBACK_POLL_INTERVAL);

      } catch (err) {
        console.error('[useActivityFeed] Error:', err);
        if (!cancelled.current) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    init();

    // Refetch + resubscribe when tab regains focus
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelled.current && !isDemoRef.current) {
        // Resubscribe (old channel may be dead after background)
        subscribe(resolvedTenantId, cancelled);
        // Refetch to catch anything missed
        fetchActivities(resolvedTenantId)
          .then(data => { if (!cancelled.current) setActivities(data); })
          .catch(err => console.error('[useActivityFeed] Visibility refetch error:', err));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled.current = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [resolvedTenantId, limit, fetchActivities, subscribe]);

  return { activities, loading, error };
}

/**
 * Format timestamp to relative time (Now, 2m, 5m, 1h, etc.)
 */
export function formatRelativeTime(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return then.toLocaleDateString();
}

/**
 * Get icon component based on event type
 * Returns Lucide icon name for use with lucide-react
 */
export function getActivityIcon(eventType) {
  const iconMap = {
    // Lead lifecycle
    lead_hot: 'Flame',
    lead_engaging: 'MessageCircle',
    lead_qualified: 'CheckCircle', // retired — kept so historical rows still render
    lead_disqualified: 'XCircle',
    lead_reengaged: 'RefreshCw',
    lead_opted_out: 'UserX',
    
    // Conversation signals
    first_reply: 'MessageCircle',
    high_sentiment: 'Heart',
    pricing_inquiry: 'DollarSign',
    timeline_mentioned: 'Clock',
    
    // AI behavior
    handoff_triggered: 'UserCheck',
    ai_disabled: 'BotOff',
    
    // Booking
    booking_in_progress: 'Calendar',
    booking_confirmed: 'CalendarCheck',
    
    // Sales rep actions
    call_logged: 'Phone',
    manual_sms: 'MessageSquare',
    ai_sms_sent: 'MessageSquare',
    
    // Campaign/system
    campaign_launched: 'Rocket',
    campaign_paused: 'Pause',
    campaign_resumed: 'Play',
    bulk_upload_completed: 'Upload',
    leads_transferred: 'ArrowRightLeft',
    
    // Default
    default: 'Activity'
  };

  return iconMap[eventType] || iconMap.default;
}

/**
 * Get color class based on event type
 */
export function getActivityColor(eventType) {
  const colorMap = {
    // Hot/urgent - red/orange
    lead_hot: '#EF4444',
    pricing_inquiry: '#F97316',
    timeline_mentioned: '#F97316',
    handoff_triggered: '#F97316',
    
    // Positive - green
    lead_qualified: '#22C55E',
    high_sentiment: '#22C55E',
    booking_confirmed: '#22C55E',
    campaign_resumed: '#22C55E',
    bulk_upload_completed: '#22C55E',
    
    // Neutral/info - blue
    first_reply: '#3B82F6',
    lead_engaging: '#3B82F6',
    booking_in_progress: '#3B82F6',
    call_logged: '#3B82F6',
    manual_sms: '#3B82F6',
    ai_sms_sent: '#3B82F6',
    lead_reengaged: '#3B82F6',
    
    // Special - purple
    campaign_launched: '#A855F7',
    leads_transferred: '#A855F7',
    
    // Inactive - gray
    lead_disqualified: '#6B7280',
    lead_opted_out: '#6B7280',
    ai_disabled: '#6B7280',
    campaign_paused: '#EAB308',
    
    // Default
    default: '#6B7280'
  };

  return colorMap[eventType] || colorMap.default;
}